import valueParser from "postcss-value-parser";
import type { Node as ValueNode } from "postcss-value-parser";

import { parseColor, rgbToHex } from "./dark-mode-color-math";
import { type ColorRole, transformColor } from "./dark-mode-clients";

const DARK_BG_HEX = "#121212";
const LIGHT_FG_HEX = "#e8e8e8";

// CSS value tokens that name a color when they appear as a bare `word`.
// Anything outside this set is left alone — keeps us from rewriting URLs
// like `red-banner.png`, custom-property names, or hyphenated identifiers
// that incidentally contain a color word.
const NAMED_COLOR_WORDS = new Set([
  "black",
  "white",
  "red",
  "green",
  "blue",
  "yellow",
  "cyan",
  "magenta",
  "gray",
  "grey",
  "silver",
  "maroon",
  "olive",
  "lime",
  "aqua",
  "teal",
  "navy",
  "fuchsia",
  "purple",
  "orange",
  "pink",
  "brown",
  "transparent",
]);

const HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

function inferRoleFromProperty(property: string): ColorRole {
  const p = property.toLowerCase();
  if (p.includes("background") || p === "bgcolor" || p === "bg") {
    return "background";
  }
  if (p.includes("border") || p.includes("outline")) {
    return "border";
  }
  return "text";
}

/**
 * Walk a parsed CSS value and apply the dark-mode transform to every token
 * that represents a color. Non-color tokens (URLs, custom property names,
 * gradient stops at non-color positions, quoted strings, units) are left
 * untouched. Goes through postcss-value-parser instead of regex so we don't
 * trip over substrings like `red-banner.png` in `url(...)` or hyphenated
 * identifier components.
 */
function transformColorsInValue(value: string, role: ColorRole): string {
  const parsed = valueParser(value);

  const transformOne = (raw: string): string | null => {
    const rgb = parseColor(raw);
    if (!rgb) return null;
    // a===0 means fully transparent. Skip the transform so we don't
    // materialize a visible block where the marketer intended a void.
    if (rgb.a === 0) return raw;
    const transformed = transformColor(rgb, role);
    // Preserve alpha on the output if it was set on the input.
    if (rgb.a !== undefined && rgb.a < 1) {
      transformed.a = rgb.a;
    }
    return rgbToHex(transformed);
  };

  const visit = (nodes: ValueNode[]): void => {
    for (const node of nodes) {
      if (node.type === "function") {
        const name = node.value.toLowerCase();
        // `url(...)` contents are paths/identifiers, never colors. Skip.
        if (name === "url") continue;
        // `rgb()`/`rgba()`/`hsl()`/`hsla()` reconstruct as a single color
        // token. Try parsing the full function and replace if it parses.
        if (
          name === "rgb" ||
          name === "rgba" ||
          name === "hsl" ||
          name === "hsla"
        ) {
          const serialized = valueParser.stringify(node);
          const next = transformOne(serialized);
          if (next !== null) {
            // postcss-value-parser only treats `word`/`string` nodes as
            // primitives; rewrite the function node into a `word` so the
            // serializer emits the hex output directly.
            (node as unknown as { type: string }).type = "word";
            (node as unknown as { value: string }).value = next;
            (node as unknown as { nodes?: unknown }).nodes = undefined;
          }
          continue;
        }
        // For other functions (linear-gradient, radial-gradient,
        // calc, etc.) recurse — color stops inside gradients are
        // normal color tokens at this level.
        if (node.nodes) visit(node.nodes);
        continue;
      }
      if (node.type === "word") {
        const lowered = node.value.toLowerCase();
        if (HEX_COLOR_RE.test(node.value) || NAMED_COLOR_WORDS.has(lowered)) {
          const next = transformOne(node.value);
          if (next !== null) node.value = next;
        }
      }
      // string/space/div nodes are pass-through.
    }
  };

  visit(parsed.nodes);
  return valueParser.stringify(parsed.nodes);
}

function transformInlineStyle(style: string): string {
  return style.replace(
    /([a-zA-Z-]+)\s*:\s*([^;]+)/g,
    (full, prop: string, val: string) => {
      const role = inferRoleFromProperty(prop);
      const newVal = transformColorsInValue(val, role);
      return `${prop}: ${newVal}`;
    },
  );
}

function transformStyleBlock(css: string): string {
  return css.replace(
    /([a-zA-Z-]+)\s*:\s*([^;{}]+)/g,
    (full, prop: string, val: string) => {
      const role = inferRoleFromProperty(prop);
      const newVal = transformColorsInValue(val, role);
      if (newVal === val) return full;
      return `${prop}: ${newVal}`;
    },
  );
}

const BG_ATTRS = ["bgcolor"];
const TEXT_ATTRS = ["color", "text", "link", "vlink", "alink"];

function transformHtmlAttributes(html: string): string {
  const allAttrs = [...BG_ATTRS, ...TEXT_ATTRS];
  const attrPattern = new RegExp(
    `\\b(${allAttrs.join("|")})\\s*=\\s*"([^"]*)"`,
    "gi",
  );

  return html.replace(attrPattern, (full, attr: string, value: string) => {
    const role = BG_ATTRS.includes(attr.toLowerCase()) ? "background" : "text";
    const newValue = transformColorsInValue(value, role);
    return `${attr}="${newValue}"`;
  });
}

function transformInlineStyles(html: string): string {
  return html.replace(/style\s*=\s*"([^"]*)"/gi, (full, styleValue: string) => {
    const newStyle = transformInlineStyle(styleValue);
    return `style="${newStyle}"`;
  });
}

function transformEmbeddedStyles(html: string): string {
  return html.replace(
    /(<style[^>]*>)([\s\S]*?)(<\/style>)/gi,
    (full, openTag: string, css: string, closeTag: string) => {
      return openTag + transformStyleBlock(css) + closeTag;
    },
  );
}

function setDarkBackground(html: string): string {
  const hasHtmlBg = /(<html[^>]*style\s*=\s*")([^"]*)/i.test(html);
  if (hasHtmlBg) {
    return html.replace(
      /(<html[^>]*style\s*=\s*")([^"]*)/i,
      (full, prefix: string, style: string) => {
        const newStyle = style.replace(/background(-color)?\s*:[^;]*/gi, "");
        return `${prefix}background-color:${DARK_BG_HEX};${newStyle}`;
      },
    );
  }

  return html.replace(
    /<html([^>]*)>/i,
    `<html$1 style="background-color:${DARK_BG_HEX};">`,
  );
}

function setBodyTextColor(html: string): string {
  const hasBodyStyle = /(<body[^>]*style\s*=\s*")([^"]*)/i.test(html);
  if (hasBodyStyle) {
    return html.replace(
      /(<body[^>]*style\s*=\s*")([^"]*)/i,
      (full, prefix: string, style: string) => {
        // Strip ONLY top-level `color:` declarations. The naive `\bcolor:`
        // pattern also matches inside `background-color:`, leaving a
        // dangling `background-` orphan. Anchor at either the start of the
        // style string or the `;` between declarations.
        const newStyle = style.replace(
          /(^|;)\s*color\s*:[^;]*/gi,
          (_m, sep: string) => sep,
        );
        return `${prefix}color:${LIGHT_FG_HEX};${newStyle}`;
      },
    );
  }

  return html.replace(
    /<body([^>]*)>/i,
    `<body$1 style="color:${LIGHT_FG_HEX};">`,
  );
}

export function transformHtmlForDarkMode(html: string): string {
  let result = html;
  result = transformEmbeddedStyles(result);
  result = transformInlineStyles(result);
  result = transformHtmlAttributes(result);
  result = setDarkBackground(result);
  result = setBodyTextColor(result);
  return result;
}
